/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IRequestContextUser } from '@msdyn365-commerce/core-internal/dist/types/interfaces/context/IRequestContext';
import { IWindow } from '../interfaces';
import { IChatContext } from '../../interfaces';
import { IMsdyn365CsChatConnectorViewProps } from '../msdyn365-cs-chat-connector';
import { ProactiveTriggers } from '../proactive';

/**
 * Chat class - generates chat context, and checks for proactive triggers, starts the chat.
 */
export default class Chat {
    public props: IMsdyn365CsChatConnectorViewProps;
    private user: IRequestContextUser;
    constructor(props: IMsdyn365CsChatConnectorViewProps) {
        this.props = props;
        this.user = props.context.request.user;
    }

    /**
     * Generates chat context, and checks for proactive triggers, starts the chat.
     */
    public async run(): Promise<void> {
        try {
            const chatContext = this._getContext();
            const chatHasStarted: boolean = await this._proactiveCheck(chatContext);
            (<IWindow>(<unknown>window)).contextProvider = () => {
                return chatContext;
            };

            this.props.context.telemetry.debug(`[Chat] Proactive chat was started: '${chatHasStarted}'.`);
            if (!chatHasStarted) {
                this.props.context.telemetry.debug('[Chat] Chat widget "lcw:ready" event listener has been triggered.');
                (<IWindow>(<unknown>window)).Microsoft.Omnichannel.LiveChatWidget.SDK.setContextProvider(() => chatContext);
            }
        } catch (e) {
            // Use ?debug=true to see telemetry
            // If previous block fails, chat will still be available (no need to display any errors to user)
            this.props.telemetry.exception(e as Error);
        }
    }

    /**
     * Generates default chat context.
     * @returns {IChatContext} The chat context.
     */
    private _getContext(): IChatContext {
        return {
            Name: {
                value: this.user.name ? this.user.name : '',
                isDisplayable: true
            },
            AccountNumber: {
                value: this.user.customerAccountNumber ? this.user.customerAccountNumber : '',
                isDisplayable: true
            },
            Email: {
                value: this.user.emailAddress ? this.user.emailAddress : '',
                isDisplayable: true
            },
            IsB2b: {
                value: this.user.isB2b ? this.user.isB2b : 'false',
                isDisplayable: true
            },
            CurrentPageURL: {
                value: window.location.href,
                isDisplayable: true
            },
            eCommerceOperatingUnitNumber: {
                value: this.props.context.request.apiSettings.oun,
                isDisplayable: false
            },
            eCommerceBaseUrl: {
                value: this.props.context.request.apiSettings.baseUrl,
                isDisplayable: false
            },
            eCommerceDataAreaId:
                this.props.context.request.channel?.InventLocationDataAreaId === undefined
                    ? undefined
                    : {
                          value: this.props.context.request.channel.InventLocationDataAreaId,
                          isDisplayable: false
                      }
        };
    }

    /**
     * Validates proactive triggers to check if proactive chat should be initialized.
     * @param {IChatContext} chatContext The chat context.
     * @returns {Promise<boolean>} The result of whether the chat has started.
     */
    private async _proactiveCheck(chatContext: IChatContext): Promise<boolean> {
        let isChatStarted = false;
        const proactiveChatEnabled: boolean = this.props.config.proactiveChatEnabled;

        this.props.context.telemetry.debug(`[Chat] Proactive chat was enabled: '${proactiveChatEnabled}'.`);
        if (proactiveChatEnabled) {
            for (let i = 0; i < ProactiveTriggers.length; i++) {
                const proactive = new ProactiveTriggers[i](this.props, chatContext);
                // Only first detected proactive trigger gets executed
                if (await proactive.startChat()) {
                    this.props.context.telemetry.debug(`[Chat] Proactive chat detected: '${proactive.constructor.name}'.`);
                    isChatStarted = true;
                    break;
                }
            }
        }
        return isChatStarted;
    }
}
